import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, AlertTriangle, ArrowRight, Zap, Shield, TrendingUp, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
export interface VsPageLayoutProps {
  competitorName: string;
  competitorDescription: string;
  seoTitle: string;
  comparisonRows: {
    feature: string;
    mistr: 'yes' | 'no' | 'limited' | string;
    competitor: 'yes' | 'no' | 'limited' | string;
  }[];
  whyMistrWins: {
    title: string;
    desc: string;
  }[];
  cantDoList: string[];
  priceComparison: {
    competitorPrice: string;
    competitorDesc: string;
    mistrPrice: string;
    mistrDesc: string;
    note?: string;
  };
}
const renderStatus = (status: string) => {
  if (status === 'yes' || status === '✅') {
    return <div className="flex items-center gap-2 text-teal font-medium" data-id="element-928">
        <Check size={20} data-id="element-929" /> Yes
      </div>;
  }
  if (status === 'no' || status === '❌') {
    return <div className="flex items-center gap-2 text-red-cta font-medium" data-id="element-930">
        <X size={20} data-id="element-931" /> No
      </div>;
  }
  if (status.includes('Limited')) {
    return <div className="flex items-center gap-2 text-amber-400 font-medium" data-id="element-932">
        <AlertTriangle size={20} data-id="element-933" /> {status}
      </div>;
  }
  if (status.includes('❌')) {
    return <div className="flex items-center gap-2 text-red-cta font-medium" data-id="element-934">
        <X size={20} data-id="element-935" /> {status.replace('❌', '').trim()}
      </div>;
  }
  return <span className="text-white/80" data-id="element-936">{status}</span>;
};
const icons = [<Zap size={24} data-id="element-937" />, <Shield size={24} data-id="element-938" />, <TrendingUp size={24} data-id="element-939" />, <Clock size={24} data-id="element-940" />];
export function VsPageLayout({
  competitorName,
  competitorDescription,
  seoTitle,
  comparisonRows,
  whyMistrWins,
  cantDoList,
  priceComparison
}: VsPageLayoutProps) {
  return <main className="pt-20" data-id="element-941">
      {/* SEO Comment */}
      {/*
                  Title: {seoTitle}
                */}

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-942">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-943" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-944" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-945" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-946">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-947">
            <span className="eyebrow" data-id="element-948">Comparison</span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-tight mb-6" data-id="element-949">
              MINDstream vs {competitorName}.<br data-id="element-950" />
              <span className="text-gradient" data-id="element-951">No Contest.</span>
            </h1>
            <p className="text-lg md:text-xl text-steel mb-10 max-w-2xl mx-auto" data-id="element-952">
              {competitorDescription} See why business owners are switching to
              full AI business automation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-id="element-953">
              Make the Switch <ArrowRight size={20} data-id="element-954" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-955" />

      {/* Comparison Table */}
      <section className="section-padding bg-dark-bg relative" data-id="element-956">
        <div className="inner-container max-w-5xl relative z-10" data-id="element-957">
          <div className="text-center mb-12" data-id="element-958">
            <h2 className="h2-title" data-id="element-959">Feature Comparison</h2>
          </div>
          <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.5)]" data-id="element-960">
            <div className="grid grid-cols-3 bg-white/5 border-b border-white/10 p-6" data-id="element-961">
              <div className="font-poppins font-bold text-lg" data-id="element-962">Feature</div>
              <div className="font-poppins font-bold text-lg text-teal flex items-center gap-2" data-id="element-963">
                MISTR <Check size={20} data-id="element-964" />
              </div>
              <div className="font-poppins font-bold text-lg text-white/60" data-id="element-965">
                {competitorName}
              </div>
            </div>
            <div className="divide-y divide-white/5" data-id="element-966">
              {comparisonRows.map((row, i) => <div key={i} className={`grid grid-cols-3 p-6 items-center transition-colors hover:bg-white/10 ${i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'}`} data-id="element-967">
                  <div className="font-medium pr-4" data-id="element-968">{row.feature}</div>
                  <div className="border-l-2 border-teal pl-4 py-2" data-id="element-969">
                    {renderStatus(row.mistr)}
                  </div>
                  <div className="pl-4 py-2" data-id="element-970">
                    {renderStatus(row.competitor)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-971" />

      {/* Why MR Wins */}
      <section className="py-24 px-6 relative bg-white" data-id="element-972">
        <div className="max-w-4xl mx-auto" data-id="element-973">
          <div className="text-center mb-16" data-id="element-974">
            <span className="text-purple text-sm font-poppins font-bold uppercase tracking-widest" data-id="element-975">
              The Difference
            </span>
            <h2 className="h2-title" data-id="element-976">Why MR Wins</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-977">
            {whyMistrWins.map((point, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative bg-white border border-gray-200 rounded-3xl p-10 shadow-sm hover:shadow-xl hover:border-purple/30 transition-all group" data-id="element-978">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-purple to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-979" />

                <div className="mb-8" data-id="element-980">
                  <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-2" data-id="element-981">
                    MR Pro
                  </h3>
                  <div className="flex items-baseline gap-2" data-id="element-982">
                    <span className="text-5xl font-poppins font-extrabold text-gray-900" data-id="element-983">
                      $997
                    </span>
                    <span className="text-gray-500 font-medium" data-id="element-984">/month</span>
                  </div>
                </div>
                <div className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-985">
                  {point.desc}
                </div>
                <div className="flex items-center justify-center gap-4" data-id="element-986">
                  <div className="w-12 h-12 rounded-full bg-purple/10 border border-purple/20 flex items-center justify-center" data-id="element-987">
                    <Zap size={24} className="text-purple" data-id="element-988" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-teal/10 border border-teal/20 flex items-center justify-center" data-id="element-989">
                    <Shield size={24} className="text-teal" data-id="element-990" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green/10 border border-green/20 flex items-center justify-center" data-id="element-991">
                    <TrendingUp size={24} className="text-green" data-id="element-992" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue/10 border border-blue/20 flex items-center justify-center" data-id="element-993">
                    <Clock size={24} className="text-blue" data-id="element-994" />
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-red-cta/30 to-transparent" data-id="element-995" />

      {/* What They Can't Do */}
      <section className="section-padding bg-dark-bg relative" data-id="element-996">
        <div className="inner-container max-w-4xl relative z-10" data-id="element-997">
          <div className="bg-gradient-to-br from-red-cta/10 to-transparent backdrop-blur-sm border border-red-cta/30 rounded-2xl p-8 md:p-12 shadow-[0_0_40px_rgba(255,0,10,0.1)]" data-id="element-998">
            <h2 className="font-poppins font-bold text-3xl mb-6 flex items-center gap-3" data-id="element-999">
              <X className="text-red-cta" size={32} data-id="element-1000" /> What {competitorName}{' '}
              Can't Do
            </h2>
            <ul className="space-y-4" data-id="element-1001">
              {cantDoList.map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-white/90" data-id="element-1002">
                  <span className="text-red-cta mt-1 font-bold" data-id="element-1003">•</span> {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1004" />

      {/* Price Comparison */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1005">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1006" />

        <div className="inner-container max-w-5xl relative z-10" data-id="element-1007">
          <div className="text-center mb-16" data-id="element-1008">
            <h2 className="h2-title" data-id="element-1009">The Bottom Line</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-1010">
            <div className="bg-gradient-to-br from-white/[0.03] to-transparent backdrop-blur-sm border border-white/10 rounded-2xl p-8 opacity-80" data-id="element-1011">
              <h3 className="font-poppins font-bold text-2xl mb-2 text-white/60" data-id="element-1012">
                {competitorName}
              </h3>
              <div className="text-4xl font-bold mb-4 text-white/80" data-id="element-1013">
                {priceComparison.competitorPrice}
              </div>
              <p className="text-steel mb-6" data-id="element-1014">
                {priceComparison.competitorDesc}
              </p>
              <button className="w-full py-3 rounded-lg border border-white/20 text-white/50 font-semibold cursor-not-allowed" data-id="element-1015">
                Keep {competitorName}
              </button>
            </div>
            <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-sm border-2 border-teal rounded-2xl p-8 shadow-[0_0_40px_rgba(7,133,183,0.3)] transform md:-translate-y-4 relative overflow-hidden" data-id="element-1016">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal/20 rounded-full blur-2xl pointer-events-none" data-id="element-1017" />
              <div className="inline-block bg-teal text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full mb-4 relative z-10" data-id="element-1018">
                The Full Ecosystem
              </div>
              <h3 className="font-poppins font-bold text-2xl mb-2" data-id="element-1019">MR Pro</h3>
              <div className="text-4xl font-bold mb-4" data-id="element-1020">
                {priceComparison.mistrPrice}
              </div>
              <p className="text-teal mb-6 font-medium" data-id="element-1021">
                {priceComparison.mistrDesc}
              </p>
              <Link to="/contact" className="btn-primary w-full block text-center" data-id="element-1022">
                Talk to MR →
              </Link>
            </div>
          </div>
          {priceComparison.note && <p className="text-center text-steel mt-8 text-sm italic max-w-2xl mx-auto" data-id="element-1023">
              * {priceComparison.note}
            </p>}
        </div>
      </section>
    </main>;
}